import React from 'react'
import { withStyles } from '@material-ui/core'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'

import { clearAtom } from '../flowBuilder/actions/atom'
import { setActiveBot } from './actions/activeBot'
import { getBots, listBots, isBotListServerSortKey } from './api/bots'
import { getBulkUsage, supervisionModeToConfig, resolveQcsLandingTab } from './api/usageLimits'
import { setQcsActivation } from './api/qcsActivation'
import { alertError, alertSuccess } from '../../api'
import { fetchPackages } from './helpers/packageTiers'
import { cachePackageDetailsFromBots } from './helpers/orgPackages'
import Bots from './components/Bots'
import BotsToolbar from './components/BotsToolbar'
import OrganisationsTable from './components/OrganisationsTable'
import OrganisationsGrid from './components/OrganisationsGrid'
import PackageCreationButton from './components/PackageCreationButton'
import OrganisationsOverview from './components/OrganisationsOverview'
import OrganisationScopeSwitch from './components/OrganisationScopeSwitch'
import QcsDevFlagPanel from './components/QcsDevFlagPanel'
import ViewToggle, { VIEW } from './components/ViewToggle'
import ManageLimitsModal from './components/ManageLimitsModal'
import Pagination from '../../uiKit/table/Pagination'
import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_GRID_ROWS_PER_PAGE,
  GRID_ROWS_PER_PAGE_OPTIONS,
  isAllRowsSelection,
  normalizePageSize,
  parseRowsPerPageSelection,
} from '../../constants/pagination'
import { HOME_PATH } from '../../configs'
import { toOrganisationRow } from './helpers/organisationFields'
import { writePlatformCounts, getInitialPlatformCountState } from './helpers/orgPlatformCounts'
import {
  PLATFORM_FLAG,
  countQcsDevOverrideOnly,
  getQcsDevOverrideIds,
  isQcsDevOverrideOnly,
  isQcsAccountUser,
  isQcsBotPayload,
  qcsAccountLandingPath,
  shouldShowQcsScopeSegment,
  shouldSkipQcsOrganisationsHome,
} from '../../constants/qcs'
import { applyBrandTheme } from '../../constants/qcsTheme'
import { isQcsHandoffSession } from '../authorization/helpers/qcsHandoffSession'
import { ROLE, ROLE_OPTIONS, access, getUserRole, hasAdminAccessOnAnyQcsBot } from 'security'
import LoaderScreen from '../../uiKit/loaders/loaderScreen'

const styles = () => ({})

const SORT_KEYS = {
  NAME: 'name',
  ACCESS: 'access',
  PACKAGE_NAME: 'packageName',
  AUDITS: 'carePlanAudits',
  SESSIONS: 'supervisionSessions',
}

const DEFAULT_FILTERS = {
  search: '',
  appliedSearch: '',
  roleFilter: 'all',
}

const VIEW_STORAGE_KEY = 'qcsOrganisationsView'

const readStoredView = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY)
    return stored === VIEW.TABLE || stored === VIEW.GRID ? stored : VIEW.TABLE
  } catch {
    // Private browsing and locked-down profiles throw on localStorage access;
    // a view preference is not worth failing the page over.
    return VIEW.TABLE
  }
}

const defaultPageSizeForView = view =>
  (view === VIEW.GRID ? DEFAULT_GRID_ROWS_PER_PAGE : DEFAULT_ROWS_PER_PAGE)

const hasOverviewAggregates = result =>
  typeof result?.totalOrganizations === 'number' ||
  typeof result?.activeOrganizations === 'number' ||
  typeof result?.packagesInUse === 'number'

/** Keeps the previous value for any aggregate the response omits. */
const mergeOverviewStats = (prevStats, result) => ({
  totalOrganizations:
    typeof result.totalOrganizations === 'number'
      ? result.totalOrganizations
      : prevStats?.totalOrganizations,
  activeOrganizations:
    typeof result.activeOrganizations === 'number'
      ? result.activeOrganizations
      : prevStats?.activeOrganizations,
  packagesInUse:
    typeof result.packagesInUse === 'number'
      ? result.packagesInUse
      : prevStats?.packagesInUse,
})

/**
 * QCS organisations home.
 *
 * List data comes from GET /bot/list?platformFlag=qcs. getBots still runs so
 * Redux stays warm for create/clone, id lookups, and the DEV "Mark as QCS"
 * panel. Override-marked bots are merged into this list client-side because
 * the API does not know about localStorage flags.
 */
class QcsHome extends React.Component {
  state = {
    ...DEFAULT_FILTERS,
    // No sort until a column header is clicked — first /bot/list omits sortBy.
    sortBy: null,
    sortDir: null,
    page: 0,
    rowsPerPage: defaultPageSizeForView(readStoredView()),
    view: readStoredView(),
    usageByBotId: {},
    limitsFor: null,
    togglingBotId: null,
    listItems: [],
    listTotal: 0,
    listReady: false,
    listLoading: false,
    // QCS list-root estate aggregates (totalOrganizations / activeOrganizations /
    // packagesInUse). Kept across filtered loads when the response omits them.
    overviewStats: null,
    // Re-read on each remount so Care ↔ QCS does not flash 0 after a prior fetch.
    ...getInitialPlatformCountState(),
    // Bumped when DEV overrides change so getRows re-reads localStorage.
    overrideTick: 0,
  }

  constructor(props) {
    super(props)
    // Apply before first paint when possible — reduces CareBrain→QCS palette flash.
    if (!shouldSkipQcsOrganisationsHome(props.adminUser)) {
      applyBrandTheme(true)
    }
  }

  componentDidMount() {
    // One-org Careco Admin / Care Manager never see this list — skip fetches
    // so a /qcs bookmark does not paint the table before the replace-redirect.
    if (shouldSkipQcsOrganisationsHome(this.props.adminUser)) return

    // Re-assert in case adminUser arrived after the constructor skip check.
    applyBrandTheme(true)
    getBots()
    this.loadList()
    this.loadPlatformCounts()
    // Warm the packages cache so organisation rows can resolve package name/tier
    // pills without waiting for Manage Limits / TierSelect to open.
    fetchPackages()
      .then(() => this.setState(prev => ({ overrideTick: prev.overrideTick + 1 })))
      .catch(() => null)
  }

  componentDidUpdate(_prevProps, prevState) {
    const pageChanged =
      prevState.page !== this.state.page ||
      prevState.rowsPerPage !== this.state.rowsPerPage ||
      prevState.appliedSearch !== this.state.appliedSearch ||
      prevState.roleFilter !== this.state.roleFilter
    const serverSortChanged =
      (prevState.sortBy !== this.state.sortBy || prevState.sortDir !== this.state.sortDir) &&
      isBotListServerSortKey(this.state.sortBy)

    if (pageChanged || serverSortChanged) {
      this.loadList({ showLoader: true })
    }
  }

  componentWillUnmount() {
    // Skip users never applied the list palette; do not clear the bot theme.
    if (!shouldSkipQcsOrganisationsHome(this.props.adminUser)) {
      // Leaving for the CareBrain home returns to the default palette.
      applyBrandTheme(false)
    }
    clearTimeout(this.searchTimer)
    this.usageController?.abort()
    this.listController?.abort()
    this.countController?.abort()
    this.statsController?.abort()
  }

  loadList = ({ showLoader = false } = {}) => {
    const { page, rowsPerPage, appliedSearch, sortBy, sortDir, roleFilter } = this.state
    this.listController?.abort()
    const controller = new AbortController()
    this.listController = controller
    if (showLoader) this.setState({ listLoading: true })

    listBots({
      page,
      size: normalizePageSize(rowsPerPage),
      platformFlag: PLATFORM_FLAG.QCS,
      search: appliedSearch,
      sortBy,
      sortDir,
      role: roleFilter,
      signal: controller.signal,
    })
      .then(result => {
        if (controller.signal.aborted) return
        const items = result.items || []
        const total = result.total || 0
        // List embeds packageDetails — warm cache before rows/usage read it.
        cachePackageDetailsFromBots(items)
        // Unfiltered list total is the QCS scope badge — avoid a second size=1
        // /bot/list for the same platform. Keep qcsCount when search/role narrow the table.
        const listUnfiltered = roleFilter === 'all' && !String(appliedSearch || '').trim()
        // Estate aggregates are account-scoped — only refresh on the unfiltered
        // list, same as qcsCount, so search/role filters cannot shrink the cards.
        const hasOverviewStats = listUnfiltered && hasOverviewAggregates(result)
        this.setState(
          prev => {
            const qcsCountFetched = listUnfiltered ? true : prev.qcsCountFetched
            const qcsCount = listUnfiltered ? total : prev.qcsCount
            if (listUnfiltered) writePlatformCounts({ qcs: total })
            return {
              listItems: items,
              listTotal: total,
              listReady: true,
              listLoading: false,
              ...(listUnfiltered ? { qcsCount } : null),
              qcsCountFetched,
              countsReady: prev.careCountFetched && qcsCountFetched,
              ...(hasOverviewStats
                ? { overviewStats: mergeOverviewStats(prev.overviewStats, result) }
                : null),
            }
          },
          () => this.loadUsage(),
        )
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        this.setState({ listReady: true, listLoading: false })
      })
  }

  /**
   * Re-reads the account-wide overview aggregates without touching the table —
   * unfiltered so search/role cannot shrink the cards, own controller so it
   * never aborts an in-flight loadList.
   */
  refreshOverviewStats = () => {
    this.statsController?.abort()
    const controller = new AbortController()
    this.statsController = controller

    listBots({ page: 0, size: 1, platformFlag: PLATFORM_FLAG.QCS, signal: controller.signal })
      .then(result => {
        if (controller.signal.aborted || !hasOverviewAggregates(result)) return
        this.setState(prev => ({ overviewStats: mergeOverviewStats(prev.overviewStats, result) }))
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
      })
  }

  /** CareBrain badge only — QCS badge comes from unfiltered loadList total. */
  loadPlatformCounts = () => {
    this.countController?.abort()
    const controller = new AbortController()
    this.countController = controller

    listBots({
      page: 0,
      size: DEFAULT_ROWS_PER_PAGE,
      platformFlag: PLATFORM_FLAG.CAREBRAIN,
      signal: controller.signal,
    })
      .then(care => {
        if (controller.signal.aborted) return
        const careCount = care.total || 0
        writePlatformCounts({ care: careCount })
        this.setState(prev => ({
          careCount,
          careCountFetched: true,
          countsReady: prev.qcsCountFetched,
        }))
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        this.setState(prev => ({
          careCountFetched: true,
          countsReady: prev.qcsCountFetched,
        }))
      })
  }

  /**
   * Usage meters from list-embedded `packageDetails` only (no GET
   * /bot/{id}/package on page load). Fresh assignment still loads on Manage
   * Limits open and after activation.
   */
  loadUsage() {
    const apiIds = (this.state.listItems || []).map(bot => bot.id)
    const overrideIds = getQcsDevOverrideIds().filter(
      id => !apiIds.some(apiId => Number(apiId) === Number(id)),
    )
    const botIds = [...apiIds, ...overrideIds]
    if (botIds.length === 0) {
      this.setState({ usageByBotId: {} })
      return
    }

    this.usageController?.abort()
    const controller = new AbortController()
    this.usageController = controller

    getBulkUsage(botIds, controller.signal).then(usageByBotId => {
      if (controller.signal.aborted) return
      this.setState({ usageByBotId: usageByBotId || {} })
    })
  }

  /**
   * The DEV panel writes localStorage only — re-render and refresh usage so
   * override-marked orgs appear on this page.
   */
  handleDevFlagChange = () => {
    this.setState(
      prev => ({ page: 0, overrideTick: prev.overrideTick + 1 }),
      () => this.loadUsage(),
    )
  }

  switchBot = bot => {
    setActiveBot(bot)
    clearAtom(0)
  }

  handleViewChange = view => {
    // Grid and table use different page-size options — reset to the new view's
    // default (keep "All") and page 1; componentDidUpdate refetches.
    this.setState(prev => {
      if (prev.view === view) return { view }
      return {
        view,
        page: 0,
        rowsPerPage: isAllRowsSelection(prev.rowsPerPage)
          ? prev.rowsPerPage
          : defaultPageSizeForView(view),
      }
    })
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view)
    } catch {
      // Preference is cosmetic — losing it is not worth surfacing.
    }
  }

  handleSearchChange = value => {
    this.setState({ search: value })
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      this.setState({ appliedSearch: value, page: 0 })
    }, 250)
  }

  handleSort = key => {
    this.setState(prev => {
      const sortDir = prev.sortBy === key && prev.sortDir === 'asc' ? 'desc' : 'asc'
      // Server-sortable columns: reset to first page so loadList refetches globally.
      // Usage columns stay client-only on the current page (no list API fields).
      if (isBotListServerSortKey(key)) {
        return { sortBy: key, sortDir, page: 0 }
      }
      return { sortBy: key, sortDir }
    })
  }

  handleRoleChange = value => {
    this.setState({ roleFilter: value, page: 0 })
  }

  handleClearFilters = () => {
    clearTimeout(this.searchTimer)
    this.setState({ ...DEFAULT_FILTERS, page: 0 })
  }

  handleManageLimits = row => {
    if (row?.role !== ROLE.ADMIN || !row.botActivated) return
    this.setState({ limitsFor: row })
  }

  /**
   * ROLE_ADMIN only — POST /bot/{id}/qcs-activation?activated=
   * Toggling resets the package cycle; the current list page is refetched in place.
   */
  handleToggleActivation = (row, activated) => {
    if (row?.role !== ROLE.ADMIN || row?.id == null) return
    const botId = row.id
    this.setState({ togglingBotId: botId })
    setQcsActivation(botId, activated)
      .then(result => {
        const botActivated = Boolean(result?.botActivated)
        this.setState(prev => ({
          listItems: (prev.listItems || []).map(bot =>
            (Number(bot.id) === Number(botId) ? { ...bot, botActivated } : bot),
          ),
        }))
        alertSuccess(botActivated ? 'Organisation activated.' : 'Organisation deactivated.', 2000)
        this.refreshOverviewStats()

        // Either direction resets the package cycle, so packageDetails, renewal and
        // carePlanAuditUsagePercentage must all come from one fresh list response.
        // listReady stays true, so the table is not swapped out and scroll is kept.
        this.loadList()
      })
      .catch(err => {
        alertError(err?.message || 'Could not update activation.')
      })
      .finally(() => {
        this.setState({ togglingBotId: null })
      })
  }

  handleLimitsSaved = (botId, limits, supervisionMode) => {
    // Reflect the new allowance immediately rather than waiting for a refetch:
    // the operator just typed the number and expects the row to agree.
    this.setState(prev => {
      const existing = prev.usageByBotId[botId] || { metrics: {} }
      const metrics = { ...existing.metrics }

      Object.entries(limits || {}).forEach(([metric, limit]) => {
        metrics[metric] = { ...(metrics[metric] || { used: 0 }), limit }
      })

      const next = {
        usageByBotId: { ...prev.usageByBotId, [botId]: { ...existing, metrics } },
      }
      // Rows are built from listItems — patch the mode there so the Supervision
      // mode column updates without waiting for another list fetch.
      if (supervisionMode) {
        const supervision = supervisionModeToConfig(supervisionMode)
        next.listItems = (prev.listItems || []).map(bot => {
          if (Number(bot.id) !== Number(botId)) return bot
          const packageDetails = bot.packageDetails
            ? { ...bot.packageDetails, supervision }
            : bot.packageDetails
          return { ...bot, supervisionMode, packageDetails }
        })
      }
      return next
    })
    // Keep Redux warm for override-only rows merged from props.bots.
    if (supervisionMode) getBots()

    // Package, limit and supervision changes also move Tier, renewal and the
    // server-computed audit %, so refetch the current page in place.
    this.refreshOverviewStats()
    this.loadList()
  }

  /** Rows carry the admin's real role for each organisation, not a guess. */
  getRows() {
    // overrideTick is read so Apply on the DEV panel forces a fresh merge.
    void this.state.overrideTick

    const toRow = bot => toOrganisationRow(bot, getUserRole(bot.id, access?.botAccesses))
    const fromApi = (this.state.listItems || []).map(toRow)
    const apiIds = new Set((this.state.listItems || []).map(bot => Number(bot.id)))
    const query = this.state.appliedSearch.trim().toLowerCase()

    const extras = (this.props.bots || [])
      .filter(isQcsDevOverrideOnly)
      .filter(bot => !apiIds.has(Number(bot.id)))
      .filter(bot => !query || String(bot.name || '').toLowerCase().includes(query))
      .map(toRow)

    return [...fromApi, ...extras]
  }

  /**
   * Share of a metric's allowance spent, for sorting.
   *
   * Ratio rather than raw count, so "19 of 20" outranks "40 of 400" — the
   * question is who is about to run out, not who has used the most.
   * Organisations with no reading sort last in either direction rather than
   * masquerading as zero usage.
   */
  usageRatio(botId, metric) {
    const entry = this.state.usageByBotId?.[botId]?.metrics?.[metric]
    if (!entry || typeof entry.used !== 'number') return -1
    if (entry.limit === null || entry.limit === undefined) return 0
    if (!entry.limit) return 1
    return entry.used / entry.limit
  }

  /**
   * Access filter is requested via GET /bot/list?role= so pagination total
   * matches. Client role filter remains a safety net if the API ignores the param.
   * Name / access / packageName / carePlanAudits are server-sorted.
   * Supervision Sessions stay client-sorted.
   */
  getVisibleRows(rows) {
    const { roleFilter, sortBy, sortDir } = this.state

    const filtered = rows.filter(row => {
      if (roleFilter !== 'all' && row.role !== roleFilter) return false
      return true
    })

    // Unsorted keeps API order; server already ordered across all pages.
    if (!sortBy || isBotListServerSortKey(sortBy)) {
      return filtered
    }

    const direction = sortDir === 'asc' ? 1 : -1

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case SORT_KEYS.SESSIONS:
          return (this.usageRatio(a.id, sortBy) - this.usageRatio(b.id, sortBy)) * direction
        default:
          return a.name.localeCompare(b.name) * direction
      }
    })
  }

  render() {
    if (!this.props.adminUser) {
      return <Bots title="Organisations" />
    }

    if (shouldSkipQcsOrganisationsHome(this.props.adminUser)) {
      // Wait for getBots so package / *FeatureUsed gates can pick the tab.
      if (!Array.isArray(this.props.bots) || this.props.bots.length === 0) {
        return <Bots title="Organisations" />
      }
      const access = this.props.adminUser.botAccesses[0]
      const botId = access?.botId
      const bot =
        botId != null
          ? this.props.bots.find(b => Number(b.id) === Number(botId)) ||
            (this.state.listItems || []).find(b => Number(b.id) === Number(botId))
          : null
      // One-org skip is QCS-only: a lone CareBrain organisation belongs on the
      // CareBrain home. Handoff sessions, and arrivals bounced from that home (it
      // counted no CareBrain organisations), keep the bot landing — Home would send them back here.
      const fromCareHome = Boolean(this.props.location?.state?.fromCareHome)
      if (!isQcsBotPayload(bot) && !isQcsHandoffSession() && !fromCareHome) {
        return <Redirect replace to={HOME_PATH} />
      }
      const skipToBot = qcsAccountLandingPath(
        this.props.adminUser,
        resolveQcsLandingTab(access.role, bot),
      )
      if (skipToBot) {
        return <Redirect replace to={skipToBot} />
      }
    }

    const {
      search,
      appliedSearch,
      roleFilter,
      sortBy,
      sortDir,
      page,
      rowsPerPage,
      view,
      usageByBotId,
      limitsFor,
      togglingBotId,
      listItems,
      listTotal,
      careCount,
      qcsCount,
      listReady,
      listLoading,
      countsReady,
    } = this.state

    const canCreatePackages = hasAdminAccessOnAnyQcsBot(
      this.props.bots,
      this.props.adminUser?.botAccesses,
    )
    const overrideOnlyCount = countQcsDevOverrideOnly(this.props.bots)
    const careCountDisplay = Math.max(0, careCount - overrideOnlyCount)
    const qcsCountDisplay = qcsCount + overrideOnlyCount

    const rows = this.getRows()
    const visibleRows = this.getVisibleRows(rows)

    const hasActiveFilters = roleFilter !== 'all' || Boolean(appliedSearch.trim())
    const listCount = listTotal + overrideOnlyCount
    // QCS handoff sessions stay on this page and never see the CareBrain tab.
    const handoffSession = isQcsHandoffSession()

    // Straight after a handoff login, a lone QCS organisation opens directly. Once only:
    // Home inside the bot comes back here without the marker, so the list stays reachable.
    const handoffLanding = handoffSession && Boolean(this.props.location?.state?.fromHandoffLogin)
    const loneBot =
      handoffLanding && listReady && !hasActiveFilters && listTotal === 1 && overrideOnlyCount === 0
        ? listItems[0]
        : null
    if (loneBot) {
      const role = getUserRole(loneBot.id, access?.botAccesses)
      return (
        <Redirect replace to={`${HOME_PATH}/bot/${loneBot.id}/${resolveQcsLandingTab(role, loneBot)}`} />
      )
    }

    // CareBrain accounts with no QCS orgs should not stay on /qcs (bookmark / stale link).
    if (
      !handoffSession &&
      countsReady &&
      listReady &&
      !isQcsAccountUser(this.props.adminUser) &&
      qcsCountDisplay === 0 &&
      !hasActiveFilters
    ) {
      return <Redirect to={HOME_PATH} />
    }

    const toolbar = (
      <BotsToolbar
        search={search}
        roleFilter={roleFilter}
        roleOptions={ROLE_OPTIONS}
        total={listReady ? qcsCountDisplay : undefined}
        shown={listReady && !listLoading && hasActiveFilters ? listCount : undefined}
        onSearchChange={this.handleSearchChange}
        onRoleChange={this.handleRoleChange}
        onClear={hasActiveFilters ? this.handleClearFilters : undefined}
        trailing={<ViewToggle value={view} onChange={this.handleViewChange} />}
      />
    )

    const listProps = {
      rows: visibleRows,
      usageByBotId,
      onOpen: this.switchBot,
      onManageLimits: this.handleManageLimits,
      onToggleActivation: this.handleToggleActivation,
      togglingBotId,
      searchTerm: appliedSearch.trim(),
      hasFilters: hasActiveFilters,
    }

    // Keep the switch mounted while counts are unknown so it does not vanish
    // when careCount is still the initial 0 before loadPlatformCounts returns.
    const showQcsSegment =
      !countsReady || shouldShowQcsScopeSegment(this.props.adminUser, qcsCountDisplay)
    const showScopeSwitch = !handoffSession && (!countsReady || careCountDisplay > 0)

    return (
      <Bots
        title="QCS organisations"
        minimalUserMenu
        toolbar={toolbar}
        summary={
          <OrganisationsOverview
            total={
              typeof this.state.overviewStats?.totalOrganizations === 'number'
                ? this.state.overviewStats.totalOrganizations
                : qcsCountDisplay
            }
            activeOrganizations={this.state.overviewStats?.activeOrganizations}
            packagesInUse={this.state.overviewStats?.packagesInUse}
            loading={!listReady && !countsReady}
          />
        }
        crossLink={
          showScopeSwitch ? (
            <OrganisationScopeSwitch
              active="qcs"
              careHref={HOME_PATH}
              qcsHref={`${HOME_PATH}/qcs`}
              careCount={countsReady ? careCountDisplay : undefined}
              qcsCount={countsReady ? qcsCountDisplay : undefined}
              showQcs={showQcsSegment}
            />
          ) : null
        }
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <QcsDevFlagPanel bots={this.props.bots} onChange={this.handleDevFlagChange} />
            {/* "New QCS organisation" hidden — creation flow isn't offered on this page. */}
            {canCreatePackages && <PackageCreationButton />}
          </div>
        }>
        {!listReady || listLoading ? (
          <LoaderScreen />
        ) : (
          <>
            {view === VIEW.GRID ? (
              <OrganisationsGrid {...listProps} />
            ) : (
              <OrganisationsTable
                {...listProps}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={this.handleSort}
              />
            )}

            {listCount > 0 && (
              <Pagination
                count={listCount}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={view === VIEW.GRID ? GRID_ROWS_PER_PAGE_OPTIONS : undefined}
                page={page}
                hasSelect
                onChangePage={(event, nextPage) => this.setState({ page: nextPage })}
                onChangeRowsPerPage={event =>
                  this.setState({
                    rowsPerPage: parseRowsPerPageSelection(event.target.value),
                    page: 0,
                  })
                }
              />
            )}
          </>
        )}

        <ManageLimitsModal
          open={Boolean(limitsFor)}
          organisation={limitsFor}
          usage={limitsFor ? usageByBotId[limitsFor.id] : null}
          onClose={() => this.setState({ limitsFor: null })}
          onSaved={this.handleLimitsSaved}
        />
      </Bots>
    )
  }
}

const mapStateToProps = state => ({
  bots: state.bots,
  adminUser: state.adminUser,
})

export default withStyles(styles, { withTheme: true })(connect(mapStateToProps)(QcsHome))
